import axios, { type InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { formatSenderApiError } from '@/lib/format-sender-api-error';
import { fetchSenderToken } from './sender-token';

declare module 'axios' {
	export interface AxiosRequestConfig {
		suppressToast?: boolean;
	}
}

type CachedToken = {
	token: string;
	expiresAt: number;
};

const REFRESH_BEFORE_MS = 60_000;

let cached: CachedToken | null = null;
let refreshPromise: Promise<string> | null = null;

export function clearSenderTokenCache() {
	cached = null;
	refreshPromise = null;
}

export async function getSenderAccessToken(force = false): Promise<string> {
	const now = Date.now();
	if (!force && cached && cached.expiresAt - REFRESH_BEFORE_MS > now) {
		return cached.token;
	}

	if (refreshPromise) {
		return refreshPromise;
	}

	refreshPromise = (async () => {
		try {
			const { token, expiresIn } = await fetchSenderToken();
			cached = {
				token,
				expiresAt: now + expiresIn * 1000,
			};
			return token;
		} finally {
			refreshPromise = null;
		}
	})();

	return refreshPromise;
}

export const senderApi = axios.create({
	baseURL: import.meta.env.VITE_SENDER_URL,
});

senderApi.interceptors.request.use(async config => {
	const token = await getSenderAccessToken();
	config.headers.Authorization = `Bearer ${token}`;
	return config;
});

senderApi.interceptors.response.use(
	response => response,
	async (error: unknown) => {
		if (!axios.isAxiosError(error)) {
			return Promise.reject(error);
		}

		const status = error.response?.status;
		const config = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
		const message = formatSenderApiError(error.response?.data, status);

		if (status === 401 && config && !config._retry) {
			config._retry = true;
			clearSenderTokenCache();
			try {
				const token = await getSenderAccessToken(true);
				config.headers.Authorization = `Bearer ${token}`;
				return senderApi(config);
			} catch {
				if (!config.suppressToast) {
					toast.error('Сессия sender истекла. Войдите снова.');
				}
				return Promise.reject(error);
			}
		}

		if (!config?.suppressToast) {
			if (status === 403) {
				toast.error(message || 'Sender отключён');
			} else {
				toast.error(message);
			}
		}

		return Promise.reject(error);
	},
);
